/**
 * Énumérations des contenus éditoriaux.
 *
 * Référence unique pour le schéma Drizzle, la validation des API et les
 * sélecteurs de formulaire : une valeur ajoutée ici se propage partout, et une
 * valeur absente est refusée aussi bien par la base que par l'API.
 */

export const contentStatuses = ['draft', 'scheduled', 'published', 'archived'] as const
export type ContentStatus = typeof contentStatuses[number]

export const visibilities = ['public', 'members'] as const
export type Visibility = typeof visibilities[number]

/** Libellés de la visibilité, lus par l'écran d'édition et par le journal. */
export const visibilityLabels: Record<Visibility, string> = {
  public: 'Public',
  members: 'Membres uniquement',
}

/**
 * Types de contenu qui portent des catégories.
 *
 * La liste vivait en dur aux deux endroits du schéma qui la contraignent —
 * `categories.type` et `content_categories.content_type` — et nulle part
 * ailleurs. Une route publique qui doit valider un type reçu n'avait donc rien
 * à quoi le comparer, et l'aurait recopiée une troisième fois : la copie qui
 * diverge ne lève aucune erreur, elle sert simplement les mauvaises rubriques.
 *
 * Distincte de `contentStatuses` par sa portée : tout contenu a un statut, mais
 * les pages, les lettres et les partenaires n'ont pas de catégorie. C'est
 * pourquoi cette liste ne se déduit pas de `contentPrefixes`, qui couvre des
 * types plus nombreux.
 */
export const categoryTypes = ['article', 'jurisprudence', 'news', 'library', 'workshop'] as const
export type CategoryType = typeof categoryTypes[number]

/**
 * Le type reçu est-il l'un de ceux que porte l'énumération ?
 *
 * Écrit comme garde de type, et non comme simple booléen : l'appelant obtient
 * une valeur utilisable telle quelle dans une requête Drizzle, sans le `as` qui
 * contournerait la vérification au moment précis où elle sert.
 */
export function isCategoryType(valeur: string): valeur is CategoryType {
  return (categoryTypes as readonly string[]).includes(valeur)
}

/**
 * Types de contenu qui peuvent porter une ou plusieurs cibles — les publics de
 * l'association (conseils syndicaux, syndics bénévoles…).
 *
 * Plus étroite que `categoryTypes` : les actualités et la législation
 * s'adressent à tous, et le client n'a pas voulu les rattacher à un public.
 * Source unique de l'ENUM `content_cibles.content_type`, des routes et du
 * serveur MCP.
 */
export const cibleContentTypes = ['article', 'jurisprudence', 'library', 'workshop'] as const
export type CibleContentType = typeof cibleContentTypes[number]

/**
 * Natures **initiales** d'un document de la librairie.
 *
 * ⚠️ Ce n'est plus la liste en vigueur : les natures vivent dans la table
 * `library_natures`, éditable depuis l'écran « Librairie › Natures ». Cette
 * liste ne sert plus qu'à décrire les huit natures que la migration
 * `20261018140000_librairie_natures_editables` a versées dans la table (et que
 * son retour remet en énumération), et de repli au journal d'activité. Ne pas
 * s'en servir pour valider ou proposer une nature : lire la table.
 *
 * Elle commande le pictogramme de la carte et le filtre de la rubrique, d'où
 * une liste fermée plutôt qu'un texte libre : deux orthographes du même mot
 * produiraient deux filtres distincts.
 *
 * L'ordre est celui des filtres du site, du plus court au plus long : les
 * fiches pratiques et techniques, et les supports d'atelier, demandés par le
 * client au rendez-vous, se rangent donc après la fiche générique — gardée,
 * les documents existants la portent.
 */
export const libraryTypes = ['fiche', 'fiche_pratique', 'fiche_technique', 'atelier', 'guide', 'synthese', 'manuel', 'livre_blanc'] as const
export type LibraryType = typeof libraryTypes[number]

/** Libellés affichés, distincts des valeurs stockées. */
export const libraryTypeLabels: Record<LibraryType, string> = {
  fiche: 'Fiche',
  fiche_pratique: 'Fiche pratique',
  fiche_technique: 'Fiche technique',
  atelier: 'Atelier',
  guide: 'Guide',
  synthese: 'Synthèse',
  manuel: 'Manuel',
  livre_blanc: 'Livre blanc',
}

/**
 * États de la **séance** d'un atelier — sa tenue, non sa publication.
 *
 * Aucune colonne ne les stocke : trois se déduisent des dates, et le quatrième
 * se lit dans `cancelledAt`. `shared/utils/workshop-status` porte ce calcul.
 * Une séance annulée le reste quelle que soit l'heure qu'il est ; les trois
 * autres états suivent le calendrier, faute de quoi un atelier d'hier
 * s'annoncerait encore « à venir ».
 *
 * À ne pas confondre avec `workshops.status`, qui porte — comme sur tout autre
 * contenu — l'état de publication tiré de `contentStatuses`.
 */
export const workshopStatuses = ['upcoming', 'ongoing', 'finished', 'cancelled'] as const
export type WorkshopStatus = typeof workshopStatuses[number]

/** Libellés affichés, distincts des valeurs stockées. */
export const workshopStatusLabels: Record<WorkshopStatus, string> = {
  upcoming: 'À venir',
  ongoing: 'En cours',
  finished: 'Terminé',
  cancelled: 'Annulé',
}

/**
 * Les douze mois, pour la parution d'une lettre mensuelle.
 *
 * Ici plutôt que dans un écran : la liste servait à la création et à
 * l'édition, chacune avec sa propre copie, et la liste en a besoin à son tour.
 * Trois copies d'une même énumération finissent par diverger.
 */
export const moisLabels: Record<number, string> = {
  1: 'Janvier',
  2: 'Février',
  3: 'Mars',
  4: 'Avril',
  5: 'Mai',
  6: 'Juin',
  7: 'Juillet',
  8: 'Août',
  9: 'Septembre',
  10: 'Octobre',
  11: 'Novembre',
  12: 'Décembre',
}

/** Les mêmes, en options de formulaire. */
export const moisOptions = Object.entries(moisLabels)
  .map(([value, label]) => ({ label, value: Number(value) }))

/**
 * Bandeau d'action en bas de page : aucun, ou celui désigné par
 * `footerCtaTemplateId`.
 *
 * Les bandeaux prédéfinis (adhésion, FNACC) étaient auparavant des valeurs de
 * cette énumération : en ajouter un exigeait une migration sur les huit tables
 * de contenu. Ce sont désormais des lignes de `cta_templates` marquées
 * `isSystem`, créées par le seed.
 */
export const footerCtaTypes = ['none', 'template'] as const
export type FooterCtaType = typeof footerCtaTypes[number]

/** Disposition du bandeau : où se place le texte par rapport à l'image. */
export const ctaLayouts = ['text-right', 'text-left', 'center'] as const
export type CtaLayout = typeof ctaLayouts[number]

/**
 * Ordre des blocs sur mobile, où le bandeau s'empile toujours.
 *
 * ⚠️ En disposition `center`, ce réglage vaut aussi pour l'ordinateur : le
 * bandeau y est empilé à toutes les tailles. `ctaCenterImagePositions` prend
 * alors le relais pour l'ordinateur, et celui-ci ne gouverne plus que le mobile.
 */
export const ctaMobileLayouts = ['text-first', 'aside-first'] as const
export type CtaMobileLayout = typeof ctaMobileLayouts[number]

/**
 * Place de l'image sur ordinateur, en disposition `center` seulement.
 *
 * Les dispositions `text-right` et `text-left` rangent l'image à côté du texte,
 * et disent déjà de quel côté. En `center`, tout est empilé : il fallait un
 * réglage propre, faute de quoi l'ordre desktop suivait celui du mobile sans
 * que rien ne l'annonce.
 *
 * `between` place l'image entre le texte et les boutons — d'où la sortie des
 * boutons hors du bloc texte quand il est retenu.
 */
export const ctaCenterImagePositions = ['after', 'before', 'between'] as const
export type CtaCenterImagePosition = typeof ctaCenterImagePositions[number]

/**
 * Position des boutons.
 *
 * ⚠️ Une image force la position `bottom` : le composant du site ignore ce
 * réglage quand `imageId` est renseigné.
 */
export const ctaButtonPositions = ['right', 'bottom'] as const
export type CtaButtonPosition = typeof ctaButtonPositions[number]

export const ogTypes = ['website', 'article', 'profile'] as const
export type OgType = typeof ogTypes[number]

export const twitterCards = ['summary', 'summary_large_image'] as const
export type TwitterCard = typeof twitterCards[number]

/**
 * Plafond de pagination des API d'administration.
 *
 * Sert aussi de borne aux écrans qui doivent charger un niveau complet
 * (réordonnancement des sous-pages) : au-delà, la liste serait tronquée sans
 * que l'utilisateur en soit averti.
 */
export const maxPageSize = 200

/** Nombre maximal de sous-pages réordonnables en une fois */
export const maxChildrenPerLevel = maxPageSize

/** Rotation autorisée pour l'icône d'en-tête, en degrés */
export const headerIconRotationRange = { min: -180, max: 180 } as const

/**
 * Préfixes d'URL du site public, par type de contenu.
 *
 * Les pages sont servies à la racine, les articles sous un segment dédié. Le
 * préfixe n'est pas stocké : la colonne `slug` ne contient que le segment final,
 * et chaque table porte déjà son propre index unique. Deux contenus de types
 * différents peuvent donc partager le même slug sans se marcher dessus.
 *
 * Centralisé ici parce que trois usages doivent s'accorder : l'affichage du
 * formulaire, la réservation du segment côté pages, et la construction des URL.
 */
export const contentPrefixes = {
  page: '',
  article: 'article',
  news: 'actualite',
  jurisprudence: 'jurisprudence',
  legislation: 'legislation',
  partner: 'partenaire',
  workshop: 'atelier',
  newsletter: 'lettre-mensuelle',
  library: 'librairie',
  press: 'presse',
} as const

/**
 * Le préfixe d'URL d'un type de catégorie, et son chemin inverse.
 *
 * ## Pourquoi une traduction, et pourquoi dans les deux sens
 *
 * Les adresses de catégorie portent un segment de type, et ce segment vit dans
 * deux registres qui ne se confondent pas :
 *
 * | | Segment de type | Public |
 * |---|---|---|
 * | URL du site | `atelier`, `actualite`, `librairie` | visiteurs, moteurs |
 * | Valeur en base | `workshop`, `news`, `library` | schéma, API |
 *
 * `link-targets.ts` compose l'adresse **publique** d'une catégorie : il lui faut
 * donc le préfixe français à l'aller (`categoryPrefix`), et la valeur
 * d'énumération au retour, quand il reconnaît une adresse déjà posée
 * (`categoryTypeFromPrefix`). Sans le second, `match()` chercherait en base un
 * type nommé « atelier », qui n'y existe pas — le lien serait affiché comme une
 * URL brute au lieu d'une référence de catégorie.
 *
 * ## Pourquoi ici plutôt que dans `link-targets.ts`
 *
 * C'est `contentPrefixes` que ces deux fonctions traversent, et une traduction
 * se lit mal loin de la table qu'elle traduit : la lecture inverse doit se
 * corriger en même temps que la table, faute de quoi un préfixe modifié
 * casserait silencieusement la reconnaissance des liens déjà posés.
 *
 * Restreintes à `CategoryType` et non ouvertes à toutes les clés de
 * `contentPrefixes` : seuls les cinq types catégorisables ont une page de
 * catégorie. Élargir inviterait à composer `/categorie/x/lettre-mensuelle`, une
 * adresse qui ne correspond à rien.
 */
export function categoryPrefix(type: CategoryType): string {
  return contentPrefixes[type]
}

/**
 * Le type de catégorie que désigne un préfixe d'URL, ou `undefined`.
 *
 * Rendu `undefined` plutôt que levant une erreur : l'appelant est `match()`,
 * qui lit des adresses saisies à la main dans le CMS. Un segment inconnu n'y est
 * pas une faute de programmation mais une adresse qui ne désigne simplement pas
 * de catégorie — elle reste alors un lien brut, ce qui est la réponse juste.
 */
export function categoryTypeFromPrefix(prefixe: string): CategoryType | undefined {
  return categoryTypes.find(type => contentPrefixes[type] === prefixe)
}

/**
 * Zones de navigation du site public, alimentées par `menu_items`.
 *
 * Un seul CRUD couvre les quatre : elles se distinguent par leur emplacement,
 * pas par la nature de ce qu'elles portent. Quatre tables auraient donné quatre
 * écrans identiques.
 *
 * `topbar` figure ici sans contenu : elle accueillera les associations
 * régionales, qui relèvent d'une table distincte (voir `PLAN-menus.md`, §2).
 *
 * ⚠️ **L'ordre de ce tableau est celui de la colonne `mysqlEnum`** : le modifier
 * réécrirait la définition SQL et exigerait une migration. Il n'a rien à voir
 * avec l'ordre d'affichage — voir `menuLocationOrder`.
 */
export const menuLocations = ['subnav', 'header', 'footer', 'topbar'] as const
export type MenuLocation = typeof menuLocations[number]

/**
 * Ordre d'affichage des zones, de haut en bas comme sur le site.
 *
 * Distinct de `menuLocations`, qui définit la colonne `mysqlEnum` et dont
 * l'ordre ne peut pas changer sans migration. Un administrateur qui cherche
 * « la barre du haut » doit la trouver en haut de la liste : l'écran suit donc
 * la page, pas la définition de la base.
 */
export const menuLocationOrder: readonly MenuLocation[] = ['topbar', 'header', 'subnav', 'footer']

/**
 * Zones dont le contenu vient d'une autre table, et non de `menu_items`.
 *
 * La barre fédération est remplie par les associations régionales
 * (`partner_associations`), qui alimentent aussi les logos du pied de page. Y
 * créer une entrée de menu produirait une ligne que le site public ne lit
 * jamais : elle n'apparaîtrait nulle part, sans que rien ne le signale.
 *
 * L'écran s'appuie dessus pour n'y proposer que la consultation, et l'API pour
 * refuser l'écriture — la règle ne peut pas reposer sur le seul formulaire.
 */
export const managedMenuLocations: readonly MenuLocation[] = ['topbar']

/** `true` quand la zone est alimentée par une autre table que `menu_items`. */
export function isManagedMenuLocation(location: MenuLocation): boolean {
  return managedMenuLocations.includes(location)
}

/** Libellés des zones, pour les filtres et la navigation de l'écran des menus. */
export const menuLocationLabels: Record<MenuLocation, string> = {
  subnav: 'Barre thématique',
  header: 'En-tête',
  footer: 'Pied de page',
  /**
   * Nommée d'après ce qu'elle porte, pas d'après sa position : « Barre
   * supérieure » ne disait rien de son contenu, et l'en-tête est tout aussi
   * haut sur la page.
   */
  topbar: 'Barre fédération',
}

/**
 * Ce que chaque zone affiche du site, en une phrase.
 *
 * L'écran d'administration ne montre qu'une liste d'intitulés : sans ce rappel,
 * rien ne dit où l'entrée que l'on déplace apparaîtra pour le visiteur.
 */
export const menuLocationHints: Record<MenuLocation, string> = {
  subnav: 'Bandeau bleu sous l\'en-tête, avec une icône par rubrique.',
  header: 'Logo ACC et navigation principale, en haut de chaque page.',
  footer: 'Colonne « Liens du site », en bas de chaque page.',
  topbar: 'Bande violette tout en haut du site : logo de la fédération et liens vers les URCC régionales.',
}

/**
 * Segments de premier niveau qu'une page ne peut pas prendre pour slug.
 *
 * Une page dont l'adresse serait « article » masquerait toutes les URL
 * d'articles côté site public, `/article/mon-titre` devenant un enfant de cette
 * page plutôt qu'un article.
 *
 * Dérivé de `contentPrefixes` plutôt qu'énuméré à la main : un type de contenu
 * ajouté là-haut réserve son segment sans édition ici. La page, servie à la
 * racine, porte un préfixe vide et n'a donc rien à réserver.
 *
 * Les pages piliers y échappent — voir `assertSlugAvailable` : « legislation »
 * est à la fois la rubrique et le préfixe de ses fiches, et les singuliers
 * retenus ici (« atelier », « partenaire ») évitent la collision avec les
 * pluriels de `system-pages.ts` dans tous les autres cas.
 */
/**
 * Segments réservés par le site sans correspondre à un type de contenu.
 *
 * `mot-cle` et `categorie` n'ont pas leur place dans `contentPrefixes`, qui
 * associe un **type de contenu** à son préfixe : ni un mot-clé ni une catégorie
 * n'est un contenu servi à une adresse, l'un et l'autre sont des segments de
 * navigation transverse. Ils n'en occupent pas moins un premier niveau d'URL, et
 * doivent être réservés au même titre.
 *
 * Ils le sont côté site (`RESERVED_PREFIXES` de `cms-routes.ts`) sans l'être
 * ici : une page dont le slug était « mot-cle » se créait sans avertissement,
 * puis répondait 404 — le site résolvant ce segment vers ses mots-clés, jamais
 * vers la page. Le rédacteur la voyait publiée dans l'administration et
 * introuvable en ligne.
 *
 * `categorie` a pris le même statut depuis que les pages de catégorie vivent
 * sous `/categorie/{slug}` et `/categorie/{slug}/{type}` (#114, PR #122) : même
 * trou, même correction.
 *
 * `server/utils/link-targets.ts` ajoutait déjà `mot-cle` à la main pour son
 * propre contrôle : la liste est désormais complète ici, à sa source. La source
 * « pages » y venant en tête du registre, `categorie` l'écarte en outre de
 * `/categorie/travaux`, qu'elle revendiquait avant la source « catégories ».
 */
const segmentsHorsContenus = ['mot-cle', 'categorie'] as const

export const reservedPageSlugs: readonly string[] = [
  ...Object.values(contentPrefixes).filter(Boolean),
  ...segmentsHorsContenus,
]

/** Longueurs maximales alignées sur les colonnes de la base */
export const fieldLimits = {
  title: 255,
  slug: 255,
  excerpt: 500,
  /*
   * Titre et description du bandeau : la limite est celle de l'écran, plus
   * courte que la colonne (255 et 1000). Au-delà, le bandeau déborde ; le
   * serveur acceptait pourtant ce que le formulaire refuse de saisir (#267).
   */
  headerTitle: 150,
  headerDescription: 600,
  headerIcon: 100,
  /**
   * Une couleur est stockée sous forme utilisable — `var(--color-x, #hex)` —
   * et non en hexadécimal seul : la plus longue de la palette fait 42
   * caractères, la colonne en accepte 120.
   */
  headerColor: 120,
  // Bandeaux d'action
  ctaName: 255,
  ctaTitle: 255,
  ctaDescription: 2000,
  ctaImageAlt: 255,
  /** Nom d'un Material Symbol, le plus long du catalogue en faisant 47 */
  ctaIconName: 100,
  ctaButtonSubtext: 255,
  ctaButtonLabel: 100,
  ctaButtonUrl: 500,
  /** Déclarations CSS libres d'un bouton, échappatoire d'un cas hors moule */
  ctaButtonCustomStyle: 500,
  // Entrées de menu
  menuLabel: 100,
  /** Une URL externe peut être longue ; la colonne suit la borne usuelle. */
  menuUrl: 2048,
  /** Nom d'icône Material Symbols (`menu_book`), pas un chemin */
  menuIcon: 100,
  // Associations régionales de la fédération
  /** Nom complet, affiché au survol : « Association Régionale des… » */
  associationName: 255,
  /** Forme courte affichée dans la barre fédération : « URCC PACA » */
  associationShortName: 100,
  associationUrl: 2048,
  /** Chemin d'un logo servi par le site, hors médiathèque */
  associationLogoPath: 500,
  metaTitle: 60,
  metaDescription: 160,
  changeSummary: 255,
  /** Markdown de la page : borne haute de sécurité, très au-delà d'un usage normal */
  content: 2_000_000,
} as const

/**
 * Contenus dont les actions sont consignées au journal.
 *
 * Tous les contenus modifiables y figurent désormais.
 * `test/activite-couverture` lie cette liste aux routes d'écriture : un
 * contenu nouvellement administrable et absent d'ici fait échouer le test,
 * plutôt que d'écrire sans laisser de trace.
 *
 * Un thème de législation y figure au même titre qu'un contenu : il se crée, se
 * renomme et se supprime, et savoir qui a déplacé une rubrique entière importe
 * autant que de savoir qui a corrigé une fiche.
 */
export const activityContentTypes = ['page', 'article', 'news', 'partner_association', 'partner', 'jurisprudence', 'newsletter', 'library', 'legislation', 'legislation_theme', 'workshop', 'press'] as const
export type ActivityContentType = typeof activityContentTypes[number]

/**
 * Nature d'une action consignée.
 *
 * Un ENUM plutôt qu'un résumé libre : la nature se filtre, se traduit et se
 * compte — `content_revisions.change_summary`, en texte, ne permet rien de tout
 * cela. Le détail circonstancié (ancienne et nouvelle valeur, nom du média)
 * vit dans la colonne `data`.
 *
 * Une sauvegarde produit **une seule** entrée, quel que soit le nombre de
 * champs touchés : `updated` porte alors la liste des changements dans `data`.
 * Les autres natures désignent des actions que l'éditeur perçoit comme
 * distinctes, et qu'il cherchera comme telles dans la frise.
 */
export const activityActions = [
  'created',
  'updated',
  'published',
  'unpublished',
  'archived',
  'deleted',
  /**
   * Retour à une version antérieure depuis l'historique des révisions.
   *
   * Distincte d'une modification : le contenu change sans que personne l'ait
   * rédigé, et c'est l'action qu'on cherchera pour comprendre un texte qui
   * « revient en arrière » tout seul.
   */
  /**
   * Enregistrement d'un brouillon, sans mise en ligne.
   *
   * Ne porte aucun détail : le journal raconte ce que le public a vu changer,
   * et un brouillon ne change rien pour lui. Le bilan des modifications revient
   * à `published`, qui compare l'ancienne version en ligne à la nouvelle.
   *
   * Deux enregistrements rapprochés du même auteur mettent à jour la même
   * entrée — sans quoi une refonte préparée sur trois jours remplirait la frise
   * avant la moindre publication.
   */
  'draft_saved',
  /**
   * Abandon d'un brouillon, sans mise en ligne.
   *
   * Porte le détail complet, à la différence de `draft_saved` : aucune
   * publication ne viendra le récapituler, et l'on doit pouvoir savoir ce que
   * le travail jeté contenait.
   */
  'draft_discarded',
  'restored',
  /**
   * Suppression d'une version depuis l'écran d'historique.
   *
   * Irréversible et réservée aux administrateurs : sans trace, une version
   * disparue resterait inexplicable — c'est précisément ce que le journal doit
   * éclairer.
   */
  'revision_deleted',
  /** Purge de tout l'historique des versions d'un contenu. */
  'revisions_purged',
  /**
   * Repli des entrées trop anciennes en une seule ligne récapitulative.
   *
   * Le journal reste sincère : on voit qu'il s'est passé quelque chose avant,
   * et combien, là où une suppression pure laisserait croire à un contenu
   * jamais modifié.
   */
  'compacted',
] as const
export type ActivityAction = typeof activityActions[number]

/**
 * Libellé que le journal donne à la couleur du bandeau d'en-tête. Partagé :
 * la frise le reconnaît pour afficher la couleur nommée plutôt que sa valeur.
 */
export const LIBELLE_COULEUR_EN_TETE = 'Couleur d\'en-tête'

/** Libellés des natures d'action, pour la frise et ses filtres. */
export const activityActionLabels: Record<ActivityAction, string> = {
  created: 'Création',
  updated: 'Modification',
  published: 'Publication',
  unpublished: 'Dépublication',
  archived: 'Archivage',
  deleted: 'Suppression',
  draft_saved: 'Brouillon enregistré',
  draft_discarded: 'Brouillon abandonné',
  restored: 'Restauration',
  revision_deleted: 'Version supprimée',
  revisions_purged: 'Historique des versions purgé',
  compacted: 'Historique condensé',
}

/**
 * Ancienneté au-delà de laquelle une entrée peut être repliée, en mois.
 *
 * Trois ans : au-delà, le détail d'une modification ne sert plus qu'à un audit,
 * pour lequel la ligne de compactage — période et compteurs par nature — suffit
 * à savoir s'il faut chercher ailleurs.
 */
export const activityCompactionMonths = 36

/**
 * Nature d'une page mesurée par l'audience (#311).
 *
 * Les contenus reprennent les noms du journal d'activité ; les pages qui n'en
 * sont pas — accueil, listes, recherche — ont les leurs. `other` recueille le
 * reste : une page inconnue est comptée plutôt que perdue.
 */
export const audienceTypes = [...activityContentTypes, 'home', 'list', 'search', 'other'] as const
export type AudienceType = typeof audienceTypes[number]

/** Durée de vie des passages détaillés, en mois — celle du cookie de mesure (CNIL). */
export const audienceRetentionMonths = 13

/** Deux passages d'une même signature sur une même page à moins de cet écart n'en font qu'un. */
export const audienceDedoublonnageMinutes = 30

/** Libellé d'une nature de page, pour les écrans d'audience. */
export const audienceTypeLabels: Record<AudienceType, string> = {
  page: 'Page',
  article: 'Article',
  news: 'Actualité',
  partner_association: 'Association partenaire',
  partner: 'Partenaire',
  jurisprudence: 'Jurisprudence',
  newsletter: 'Lettre mensuelle',
  library: 'Librairie',
  legislation: 'Législation',
  legislation_theme: 'Thème de législation',
  workshop: 'Atelier',
  press: 'Presse',
  home: 'Accueil',
  list: 'Liste',
  search: 'Recherche',
  other: 'Autre',
}

/** Écran d'édition d'un contenu, pour relier une ligne d'audience à sa fiche. */
export const audienceFicheAdmin: Partial<Record<AudienceType, string>> = {
  page: '/pages',
  article: '/articles',
  news: '/actualites',
  partner: '/partenaires',
  jurisprudence: '/jurisprudence',
  newsletter: '/lettres-mensuelles',
  library: '/librairie',
  legislation: '/legislation',
  workshop: '/ateliers',
  press: '/presse',
}
